<template>
  <modal ref="modal" :openOnLoad="false" @close="$emit('close', $event)" @open="$emit('open', $event)" class="logCallModal">
    <template slot="title">
      <template v-if="callId">
        Edit Call Details
      </template>
      <template v-else>
        Log Call
      </template>
    </template>
    <template slot="body">
      <!--Loading, while waiting on call data-->
      <div v-show="callId && !call">
        <br>
        <loading></loading>
        <br>
      </div>

      <!--Show Form-->
      <form-form @submit="handleSubmit" :submitError="submitError" :validator="$v" v-show="!callId || call">
        <div class="section">
          <div class="title">Date &amp; Phone Number</div>
          <div class="grid-x grid-margin-x">
            <div class="cell medium-6">
              <form-datepicker-element v-model="date" :pick-time="true"></form-datepicker-element>
            </div>
            <div class="cell medium-6">
              <form-select-element :options="phoneOptions" empty="Number called..." v-model="contactPhoneId" />
            </div>
          </div>
        </div>
        <!--Outcome-->
        <div class="section">
          <div class="title">Call Outcome</div>
          <div class="call-outcome-wrapper">
            <button-picker :options="callOutcomeOptions" :expanded="true" :cols="2" v-model="outcome" @change="handleOutcomeChange" />
          </div>
          <add-note-field
            placeholder="Add call notes..."
            v-model="note"
            :start-lines="2"
            ref="notesTextarea" />
        </div>

        <!--Ask / Pledge-->
        <div class="section asks" v-show="hasContributionAsk">
          <div class="title">
            <div class="grid-x align-middle">
              <div class="cell auto">
                Ask/Pledge Contribution
              </div>
              <div class="cell shrink">
                <a @click="removeContributionAsk()">
                  <span class="fas fa-times"></span>
                </a>
              </div>
            </div>
          </div>
          <log-contribution-ask
            wide
            :default-ask-amount="contact.ask_amount"
            :ask-amount="contributionAskAmount"
            :ask-result="contributionAskResult"
            :pledge-amount="contributionPledgeAmount"
            :hasAsk="hasContributionAsk"
            @hasAsk="hasContributionAsk = $event"
            ref="logContributionAsk" />
        </div>

        <!--Asks / Commitments-->
        <div class="section asks" v-show="asks.length > 0">
          <div class="title">Asks / Commitments</div>
          <log-commitment-asks ref="logCommitmentAsks" :asks="asks" wide />
        </div>

        <div v-if="didConnect">
          <br>
          <a class="button clear" :class="{ 'disabled': hasContributionAsk }" @click="addContributionAsk()"><i class="fas fa-plus-circle"></i> Ask/Pledge</a>
          <a class="button clear" @click="addAsk()" v-if="commitmentTypes && commitmentTypes.length > 0"><i class="fas fa-plus-circle"></i> Ask/Commitment</a>
        </div>

      </form-form>
    </template>

    <template slot="footer">
      <div class="grid-x align-middle">
        <div class="cell auto">
          <form-button :validator="$v" :loading="isLoading" @click="handleSubmit">
            <template v-if="callId">Update Call</template>
            <template v-else>Log Call</template>
          </form-button>
        </div>
        <div class="cell shrink" v-if="showDoNotContact">
          <form-checkbox-element label='Add to "Do Not Contact"' :class="'text-danger'" v-model="doNotContact"></form-checkbox-element>
        </div>
      </div>
    </template>
  </modal>
</template>

<script>
import Modal from '../../layouts/Modal.vue'
import moment from 'moment'
import ContactsService from '../../services/ContactsService'
import { formsMixin } from '../../mixins/forms/formsMixin'
import { required } from 'vuelidate/lib/validators'
import ButtonPicker from '../Common/ButtonPicker'
import { callQuery, logCallMutation, updateLogCallMutation } from '../../graphql/calls'
import { commitmentTypesQuery } from '../../graphql/commitments'
import AddNoteField from '../Contacts/AddNoteField'
import LogContributionAsk from './LogContributionAsk'
import LogCommitmentAsks from './LogCommitmentAsks'
import Loading from '../Common/Loading'

const callOutcomes = [
  { label: 'Left Voicemail', value: 'Voicemail' },
  { label: 'Connected', value: 'Connected', didConnect: true, showDoNotContact: true },
  { label: 'No Answer', value: 'No Answer' },
  { label: 'Left Message with Someone', value: 'Left Message' },
  { label: 'Wrong Number', value: 'Wrong Number', showDoNotContact: true },
  { label: 'Other', value: 'Other', didConnect: true, showDoNotContact: true }
]

export default {
  mixins: [
    formsMixin
  ],
  components: {
    Loading,
    LogCommitmentAsks,
    LogContributionAsk,
    AddNoteField,
    ButtonPicker,
    Modal
  },
  props: {
    callId: Number,
    contact: Object,
    phoneId: Number
  },
  data () {
    return {
      isLoading: false,
      submitError: null,

      date: moment().format('YYYY-MM-DD HH:mm'),
      contactPhoneId: null,
      outcome: null,
      note: '',
      callOutcomeOptions: callOutcomes,
      doNotContact: false,

      // Asks, Contributions, and Commitments
      hasContributionAsk: false,
      contributionAskAmount: null,
      contributionAskResult: null,
      contributionPledgeAmount: null,
      asks: []
    }
  },
  validations: {
    date: {
      required
    },
    outcome: {
      required
    }
  },
  apollo: {
    commitmentTypes: commitmentTypesQuery,
    call: {
      query: callQuery,
      variables () {
        return {
          id: this.callId
        }
      },
      result () {
        this.resetData()
      },
      skip () {
        return !this.callId
      }
    }
  },
  computed: {
    didConnect () {
      return this.outcome && this.outcome.didConnect
    },
    showDoNotContact () {
      return !this.contact.do_not_contact && (this.didConnect || (this.outcome && this.outcome.showDoNotContact))
    },
    phoneOptions () {
      return (this.contact.phones || []).map(phone => { return { value: phone.id, text: 'Called: ' + phone.number } })
    }
  },
  methods: {
    openModal () {
      this.$refs.modal.openModal()
    },
    closeModal () {
      this.$refs.modal.closeModal()
    },
    resetData () {
      this.submitError = null
      if (this.call) {
        // if there is call data, then reset the data from the call
        this.updateLocalDataWithCallData()
      } else {
        // otherwise, set to defaults
        this.date = moment().format('YYYY-MM-DD HH:mm')
        this.outcome = callOutcomes.find(outcome => outcome.value === 'Voicemail')
        this.note = ''
        this.doNotContact = false
        this.hasContributionAsk = null
        this.contributionAskAmount = null
        this.contributionAskResult = null
        this.contributionPledgeAmount = null
        this.asks = []
        this.setContactPhoneFromProp()
      }
    },
    updateLocalDataWithCallData () {
      const call = this.call
      this.date = call.date
      this.outcome = callOutcomes.find(outcome => outcome.value === call.outcome)
      this.contactPhoneId = call.contact_phone_id
      this.note = call.note ? call.note.note : ''
      this.hasContributionAsk = !!call.contributionAsk
      this.contributionAskAmount = call.contributionAsk ? (call.contributionAsk.amount || null) : null
      this.contributionAskResult = call.contributionAsk ? (call.contributionAsk.result || null) : null
      this.contributionPledgeAmount = call.contributionPledge ? (call.contributionPledge.amount || null) : null
      // creating the asks is a little tricky, because we don't actually know which value matches which specific ask.
      // though, it's not really important, as long as we match the type.
      const usedCommitments = []
      this.asks = (call.commitmentAsks || []).map(ask => {
        // find the fist matching commitment (that we haven't already used)
        const commitment = (call.commitments || []).find(commitment => {
          return commitment.commitment_type_id === ask.commitment_type_id && !usedCommitments.includes(commitment)
        })
        if (commitment) {
          usedCommitments.push(commitment)
        }
        return {
          result: ask.result,
          commitment_type_id: ask.commitment_type_id,
          value: commitment && commitment.value ? JSON.parse(commitment.value) : null
        }
      })
    },
    setContactPhoneFromProp () {
      // if a phone id was provided and matches one of the available numbers, then select it
      if (this.phoneId && this.contact.phones.find(phone => Number(phone.id) === Number(this.phoneId))) {
        this.contactPhoneId = this.phoneId
        return
      }
      // otherwise, set default to empty or the only valid phone number (if there's only one)
      const validPhones = (this.contact.phones || []).filter(phone => !phone.is_bad)
      this.contactPhoneId = validPhones.length === 1 ? validPhones[0].id : null
    },

    //
    // ASK, CONTRIBUTIONS< AND COMMITMENTS
    //
    addContributionAsk () {
      return this.$refs.logContributionAsk.addAsk()
    },
    removeContributionAsk () {
      return this.$refs.logContributionAsk.removeAsk()
    },
    isContributionAskEmpty () {
      return this.$refs.logContributionAsk.isContributionAskEmpty()
    },
    addAsk () {
      this.$refs.logCommitmentAsks.addAsk()
    },

    handleOutcomeChange () {
      // when a new outcome is selected, focus on the notes box
      if (this.$refs.notesTextarea && this.$refs.notesTextarea.focus) {
        this.$refs.notesTextarea.focus()
      }
      // if the outcome is not a "connected" outcome, then clear any empty asks
      if (!this.didConnect && this.isContributionAskEmpty()) {
        this.$refs.logContributionAsk.removeAsk()
      }
    },
    async handleSubmit () {
      this.isLoading = true
      let hasError = false

      // build the parameters
      const contactId = this.contact.id

      // CALL
      const formattedDate = moment(this.date).format('YYYY-MM-DD HH:mm:ss')
      const call = {
        date: formattedDate,
        outcome: this.outcome ? this.outcome.value : null,
        contact_phone_id: this.contactPhoneId || null
      }

      // CONTRIBUTION ASK & PLEDGE
      const { contributionAsk, contributionPledge } = this.$refs.logContributionAsk.generateLogCallMutationVariables()

      // ASKS & COMMITMENTS
      const { commitmentAsks, commitments } = this.$refs.logCommitmentAsks.generateLogCallMutationVariables()

      // assemble the variables
      const variables = {
        contactId,
        call,
        contributionAsk,
        contributionPledge,
        commitmentAsks,
        commitments,
        note: this.note.trim() || null,
        doNotContact: !!this.doNotContact
      }

      // get the appropriate mutation, depending on whether we're editing or creating the call
      let mutation = logCallMutation
      if (this.callId) {
        // add the call id to the variables
        variables.callId = this.callId
        // update the mutation
        mutation = updateLogCallMutation
      }

      // submit
      try {
        await this.$apollo.mutate({
          mutation,
          variables,
          refetchQueries: [
            ContactsService.getContactQuery(contactId)
          ]
        })
      } catch (e) {
        this.submitError = e
        hasError = true
      }

      // close the modal
      if (!hasError) {
        this.closeModal()
        // clear the form
        this.resetData()
      }

      // stop processing
      this.isLoading = false
    }
  },
  watch: {
    phoneId (newValue) {
      this.setContactPhoneFromProp()
    },
    'contact.id': {
      handler () {
        // reset the data when the contact changes
        this.resetData()
      }
    }
  },
  created () {
    // set the data defaults on load
    this.resetData()
  }
}
</script>

<style lang="scss">
@import "../../style/settings";

.logCallModal {
  .section {
    margin-bottom: 1rem;

    &.asks, &.commitments {
      .title {
        padding: .5rem;
        background: $lightest-gray;
        color: $light-font-color;
        font-size: $small-font-size;
        border-radius: $global-radius;
        text-align: center;
        margin-bottom: .5rem;
      }
      .removeIcon {
        padding-top: 0.5rem;
        padding-left: 0.5rem;
      }
    }

    .call-outcome-wrapper {
      margin-bottom: .5rem;
    }
    .donor-support-wrapper {
      margin-top: 1rem;
      padding-left: 2rem;
      padding-right: 2rem;
    }
  }
}
</style>
